import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { products, categories } from '../data/mock';
import { useCart } from '../context/CartContext';
import { ShoppingCart, Filter, X, ChevronDown } from 'lucide-react';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('featured');
  const { addToCart } = useCart();

  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => {
    document.title = 'Shop Premium Bike Accessories & Cycling Gear | EC Pedal Works';
  }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedCategory) {
      result = result.filter(p => p.categorySlug === selectedCategory);
    }
    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'name': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return result;
  }, [selectedCategory, sortBy]);

  const handleCategoryChange = (categorySlug) => {
    if (categorySlug) setSearchParams({ category: categorySlug });
    else setSearchParams({});
  };

  const handleQuickAdd = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
  };

  const currentCategory = categories.find(c => c.slug === selectedCategory);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <Header />
      <CartDrawer />

      <main>
        {/* Page Header */}
        <section className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <nav className="text-sm text-gray-500 mb-3">
              <Link to="/" className="hover:text-orange-600">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-900">{currentCategory ? currentCategory.name : 'Shop All'}</span>
            </nav>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              {currentCategory ? currentCategory.name : 'Shop All Products'}
            </h1>
            <p className="mt-2 text-gray-600 max-w-2xl">
              {currentCategory
                ? currentCategory.description
                : 'Premium bike accessories and cycling gear for passionate cyclists and riders.'}
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Filters */}
            <aside className={`lg:w-64 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              <div className="bg-white rounded-xl p-6 ring-1 ring-gray-100 shadow-sm sticky top-32">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-bold text-gray-900 text-lg">Categories</h2>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="lg:hidden text-gray-500"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-1">
                  <button
                    onClick={() => handleCategoryChange('')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      !selectedCategory
                        ? 'bg-orange-50 text-orange-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    All Products
                    <span className="float-right text-gray-400">{products.length}</span>
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedCategory === cat.slug
                          ? 'bg-orange-50 text-orange-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {cat.name}
                      <span className="float-right text-gray-400">{cat.productCount}</span>
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Products Section */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="bg-white rounded-xl p-4 ring-1 ring-gray-100 shadow-sm mb-6 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowFilters(true)}
                    className="lg:hidden inline-flex items-center gap-2 px-3 py-2 bg-orange-50 text-orange-700 rounded-lg text-sm font-medium"
                  >
                    <Filter className="w-4 h-4" />
                    Filter
                  </button>
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">{filteredProducts.length}</span> product{filteredProducts.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-white border border-gray-200 text-gray-700 text-sm rounded-lg px-4 py-2 pr-9 focus:outline-none focus:ring-2 focus:ring-orange-500 cursor-pointer"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="name">Name: A-Z</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Product Grid */}
              {filteredProducts.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center ring-1 ring-gray-100">
                  <p className="text-gray-600">No products found in this category.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProducts.map((product) => (
                    <div
                      key={product.id}
                      className="bg-white rounded-lg shadow hover:shadow-lg transition overflow-hidden flex flex-col group"
                    >
                      <Link to={`/product/${product.slug}`} className="block relative">
                        <img
                          className="w-full object-cover h-64 group-hover:scale-105 transition-transform duration-500"
                          src={product.image}
                          alt={product.name}
                        />
                        {product.badge && (
                          <span className="absolute top-3 left-3 bg-orange-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                            {product.badge}
                          </span>
                        )}
                      </Link>
                      <div className="p-4 flex flex-col flex-grow justify-between">
                        <div>
                          <Link
                            to={`/product/${product.slug}`}
                            className="block text-base font-semibold text-gray-800 hover:text-orange-600 line-clamp-2 min-h-[3rem]"
                          >
                            {product.name}
                          </Link>
                          <p className="text-sm text-gray-500 my-2 line-clamp-2">
                            {product.description.slice(0, 80)}...
                          </p>
                        </div>
                        <div>
                          <div className="text-orange-600 font-bold text-lg mb-3">
                            ${product.price.toFixed(2)}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => handleQuickAdd(e, product)}
                              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-orange-600 text-white text-sm font-medium rounded hover:bg-orange-700 transition"
                            >
                              <ShoppingCart className="w-4 h-4" />
                              Add
                            </button>
                            <Link
                              to={`/product/${product.slug}`}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-100 transition"
                            >
                              View
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShopPage;
